import { useMemo } from 'react'

import { useQuery } from '@tanstack/react-query'
import { useFormContext } from 'react-hook-form'

import { useApi } from '@fortem-monorepo/react-spa'
import { ContentBlocks } from '@fortem-monorepo/web-sdk'

import { ActiveField } from '@/pages/funds/ic-guidelines-v2/components/edit-guideline-form/fields/ActiveField'
import { AggregateField } from '@/pages/funds/ic-guidelines-v2/components/edit-guideline-form/fields/AggregateField'
import { DescriptionField } from '@/pages/funds/ic-guidelines-v2/components/edit-guideline-form/fields/DescriptionField'
import { FundsField } from '@/pages/funds/ic-guidelines-v2/components/edit-guideline-form/fields/FundsField'
import { IdentifiersField } from '@/pages/funds/ic-guidelines-v2/components/edit-guideline-form/fields/IdentifiersField'
import { NameField } from '@/pages/funds/ic-guidelines-v2/components/edit-guideline-form/fields/NameField'
import { TableField } from '@/pages/funds/ic-guidelines-v2/components/edit-guideline-form/fields/TableField'
import { ThresholdTypeField } from '@/pages/funds/ic-guidelines-v2/components/edit-guideline-form/fields/ThresholdTypeField'
import { ThresholdsField } from '@/pages/funds/ic-guidelines-v2/components/edit-guideline-form/fields/ThresholdsField'
import { ValueColumnField } from '@/pages/funds/ic-guidelines-v2/components/edit-guideline-form/fields/ValueColumnField'
import { GuidelineFormValues } from '@/pages/funds/ic-guidelines-v2/components/edit-guideline-form/schema'

export const EditGuidelineFormFields: React.FC = () => {
  const { watch } = useFormContext<GuidelineFormValues>()

  const api = useApi()

  const configColumnsQuery = useQuery({
    queryKey: ['ic-guidelines', 'blocks', 'columns'],
    queryFn: () => api.icGuidelines.v2.getConfig(),
    select(data) {
      const blocks = data as ContentBlocks.DataGridContentBlock[]

      const res = (blocks ?? []).map((block) => ({
        id: block.id,
        identifierColumns: block.columns.filter((col) => col.roles?.includes('identifier')),
        valueColumns: block.columns.filter((col) => col.roles?.includes('value')),
      }))

      return res
    },
  })

  const chosenTable = watch('table')

  type ConfigColumns = {
    identifierColumns: ContentBlocks.DataGridColumn[]
    valueColumns: ContentBlocks.DataGridColumn[]
  }

  const { identifierColumns, valueColumns }: ConfigColumns = useMemo(() => {
    const res: ConfigColumns = {
      identifierColumns: [],
      valueColumns: [],
    }

    const config = configColumnsQuery.data?.find((table) => table.id === chosenTable)

    if (config) {
      res.identifierColumns = config.identifierColumns
      res.valueColumns = config.valueColumns
    }

    return res
  }, [chosenTable, configColumnsQuery.data])

  return (
    <div className={'flex flex-col flex-1 space-y-6'}>
      <div className={'flex flex-wrap gap-6'}>
        <div className="flex-1">
          <NameField />
        </div>
        <ActiveField />
      </div>
      <DescriptionField />
      <FundsField />
      <TableField />
      <ValueColumnField valueColumns={valueColumns} />
      <AggregateField />
      <IdentifiersField identifierColumns={identifierColumns} />
      <ThresholdTypeField />
      <ThresholdsField />
    </div>
  )
}
